import React, { useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Collapse,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import SyncProblemOutlinedIcon from "@mui/icons-material/SyncProblemOutlined";
import { alpha, useTheme } from "@mui/material/styles";
import { useQuery } from "react-query";
import dayjs from "dayjs";
import { useUsers } from "../../hooks/useUsers";
import { fetchAgendaAvailability } from "../../api/client";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";

const DAY_START_HOUR = 7;
const DAY_END_HOUR = 21;
const AVAILABILITY_PANEL_OPEN_KEY = "xpro:agenda:availability-open";

function readAvailabilityPanelOpen(fallback) {
  try {
    if (typeof sessionStorage === "undefined") return fallback;
    const raw = sessionStorage.getItem(AVAILABILITY_PANEL_OPEN_KEY);
    if (raw === "1") return true;
    if (raw === "0") return false;
  } catch (_) {
    /* storage indisponível */
  }
  return fallback;
}

function writeAvailabilityPanelOpen(open) {
  try {
    if (typeof sessionStorage === "undefined") return;
    sessionStorage.setItem(AVAILABILITY_PANEL_OPEN_KEY, open ? "1" : "0");
  } catch (_) {
    /* storage indisponível */
  }
}

function unwrap(res) {
  if (res && typeof res === "object" && "data" in res) return res.data;
  return res;
}

function parseBlockTime(value) {
  return dayjs(String(value || "").replace(" ", "T"));
}

function normalizeBusyBlocks(busy = []) {
  const seen = new Set();
  const out = [];
  for (const block of busy) {
    const start = parseBlockTime(block.start);
    const end = parseBlockTime(block.end);
    if (!start.isValid() || !end.isValid() || !end.isAfter(start)) continue;
    const key = `${start.valueOf()}|${end.valueOf()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ start, end, source: block.source || null });
  }
  return out.sort((a, b) => a.start.valueOf() - b.start.valueOf());
}

function displayNameOf(person) {
  return person?.name || person?.email || `Usuário #${person?.userId}`;
}

function getInitials(name = "") {
  const parts = String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
}

function avatarTone(seed = "", theme) {
  const tones = [
    theme.palette.primary.main,
    theme.palette.info.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.secondary?.main || theme.palette.primary.dark,
  ];
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash + seed.charCodeAt(i) * (i + 1)) % 997;
  return tones[hash % tones.length];
}

function getPersonAvailability(user) {
  const now = dayjs();
  const dayStart = now.startOf("day");
  const endOfToday = now.endOf("day");
  const blocks = normalizeBusyBlocks(user?.busy);
  const current = blocks.find((b) => !b.start.isAfter(now) && b.end.isAfter(now)) || null;
  const upcomingToday = blocks.filter((b) => b.end.isAfter(now) && b.start.isBefore(endOfToday));
  const todayBlocks = blocks.filter((b) => b.end.isAfter(dayStart) && b.start.isBefore(endOfToday));
  const nextBlock = upcomingToday[0] || null;
  const needsReauth = user?.error === "needs_reauth";

  let status = "free";
  if (current) status = "busy";
  else if (upcomingToday.length) status = "later";

  return { status, current, upcomingToday, nextBlock, needsReauth, todayBlocks };
}

function statusColor(status, theme) {
  if (status === "busy") return theme.palette.error.main;
  if (status === "later") return theme.palette.warning.main;
  return theme.palette.success.main;
}

function statusSortRank(status) {
  if (status === "busy") return 0;
  if (status === "later") return 1;
  return 2;
}

/** Só escreve o que muda a decisão — evita “Disponível o dia todo” repetido. */
function personInsight(person) {
  if (person.status === "busy" && person.current) {
    const end = person.current.end;
    const start = person.current.start;
    const spansFullDay =
      start.hour() === 0 &&
      start.minute() === 0 &&
      end.diff(start, "hour") >= 20 &&
      (end.hour() === 0 || end.hour() === 23);
    if (spansFullDay) return "Em compromisso o dia todo";
    return `Em compromisso até ${end.format("HH:mm")}`;
  }
  if (person.status === "later" && person.nextBlock) {
    const end = person.nextBlock.end.format("HH:mm");
    return `Próximo às ${person.nextBlock.start.format("HH:mm")}–${end}`;
  }
  if ((person.todayBlocks || []).length) {
    return "Sem mais compromissos hoje";
  }
  return null;
}

function buildSummaryCopy({ busyCount, laterCount, freeCount, total, canSchedule, reauthCount }) {
  if (!total) {
    return { title: "Ninguém para mostrar", detail: "Quando houver colaboradores, o status aparece aqui." };
  }

  if (busyCount === 0 && laterCount === 0) {
    return {
      title: total === 1 ? "Disponível agora" : "Ninguém em compromisso agora",
      detail: reauthCount
        ? canSchedule
          ? "Livre agora no XPro. Algumas agendas Google estão desconectadas — confirme antes de marcar."
          : "Livre agora no XPro. Algumas agendas Google estão desconectadas."
        : canSchedule
          ? "Abra a lista e clique em alguém para agendar."
          : "Pode agendar com qualquer um da lista.",
    };
  }
  if (busyCount === 0 && laterCount > 0) {
    return {
      title: laterCount === 1 ? "Todos livres agora · 1 com horário depois" : `Todos livres agora · ${laterCount} com horário depois`,
      detail: canSchedule
        ? "Clique em alguém para abrir o agendamento."
        : "Veja abaixo quem ainda tem compromisso hoje.",
    };
  }
  const freeNow = freeCount + laterCount;
  return {
    title:
      busyCount === 1
        ? `1 em compromisso · ${freeNow} livre${freeNow === 1 ? "" : "s"}`
        : `${busyCount} em compromisso · ${freeNow} livre${freeNow === 1 ? "" : "s"}`,
    detail: canSchedule
      ? "Clique em alguém para agendar uma reunião."
      : "Use os horários para achar a melhor janela.",
  };
}

function DayStrip({ blocks, status, theme, width = 100 }) {
  const now = dayjs();
  const dayStart = now.hour(DAY_START_HOUR).minute(0).second(0).millisecond(0);
  const dayEnd = now.hour(DAY_END_HOUR).minute(0).second(0).millisecond(0);
  const totalMins = Math.max(1, dayEnd.diff(dayStart, "minute"));
  const nowPct = Math.min(100, Math.max(0, (now.diff(dayStart, "minute") / totalMins) * 100));
  const track = alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.14 : 0.07);
  const busyFill = alpha(statusColor(status === "free" ? "later" : status, theme), 0.7);

  const segments = (blocks || [])
    .map((b) => {
      const start = Math.max(0, b.start.diff(dayStart, "minute"));
      const end = Math.min(totalMins, b.end.diff(dayStart, "minute"));
      if (end <= 0 || start >= totalMins || end <= start) return null;
      return {
        left: `${(start / totalMins) * 100}%`,
        width: `${((end - start) / totalMins) * 100}%`,
        label: `${b.start.format("HH:mm")}–${b.end.format("HH:mm")}`,
      };
    })
    .filter(Boolean);

  if (!segments.length) return null;

  const tip = `Hoje · ${segments.map((s) => s.label).join(", ")}`;

  return (
    <Tooltip title={tip} enterDelay={300}>
      <Box sx={{ flexShrink: 0, width, pt: 0.15 }}>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.35, px: 0.15 }}>
          <Typography variant="caption" sx={{ fontSize: 9, color: "text.disabled", lineHeight: 1 }}>
            7h
          </Typography>
          <Typography variant="caption" sx={{ fontSize: 9, color: "text.disabled", lineHeight: 1 }}>
            21h
          </Typography>
        </Stack>
        <Box
          aria-hidden
          sx={{
            position: "relative",
            width: "100%",
            height: 7,
            borderRadius: 99,
            bgcolor: track,
            overflow: "hidden",
          }}
        >
          {segments.map((seg, idx) => (
            <Box
              key={idx}
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: seg.left,
                width: seg.width,
                bgcolor: busyFill,
              }}
            />
          ))}
          <Box
            sx={{
              position: "absolute",
              top: -1,
              bottom: -1,
              left: `${nowPct}%`,
              width: 2,
              ml: "-1px",
              bgcolor: theme.palette.text.primary,
              opacity: 0.5,
              borderRadius: 1,
            }}
          />
        </Box>
      </Box>
    </Tooltip>
  );
}

function PersonAvatar({ person, theme, size = 32, showStatus = true }) {
  const name = displayNameOf(person);
  const tone = avatarTone(name, theme);
  const color = statusColor(person.status, theme);
  return (
    <Box sx={{ position: "relative", flexShrink: 0, width: size, height: size }}>
      <Avatar
        sx={{
          width: size,
          height: size,
          fontSize: size <= 28 ? 11 : 12,
          fontWeight: 800,
          bgcolor: alpha(tone, theme.palette.mode === "dark" ? 0.28 : 0.14),
          color: tone,
        }}
      >
        {getInitials(name)}
      </Avatar>
      {showStatus ? (
        <Box
          sx={{
            position: "absolute",
            right: -1,
            bottom: -1,
            width: size <= 28 ? 9 : 10,
            height: size <= 28 ? 9 : 10,
            borderRadius: "50%",
            bgcolor: color,
            border: `2px solid ${theme.palette.background.paper}`,
          }}
        />
      ) : null}
    </Box>
  );
}

function PersonRow({ person, theme, compact, onSchedule, showStrip = true }) {
  const insight = personInsight(person);
  const hasStrip = showStrip && (person.todayBlocks || []).length > 0;
  const canSchedule = Boolean(onSchedule && String(person.email || "").trim());

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      onClick={canSchedule ? () => onSchedule(person) : undefined}
      role={canSchedule ? "button" : undefined}
      tabIndex={canSchedule ? 0 : undefined}
      onKeyDown={
        canSchedule
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onSchedule(person);
              }
            }
          : undefined
      }
      sx={{
        minWidth: 0,
        px: compact ? 0.75 : 1,
        py: compact ? 0.75 : 0.9,
        borderRadius: 2,
        cursor: canSchedule ? "pointer" : "default",
        transition: "background-color 120ms ease",
        "&:hover": {
          bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.12 : 0.05),
        },
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 1,
        },
      }}
    >
      <PersonAvatar person={person} theme={theme} size={compact ? 28 : 32} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" fontWeight={700} noWrap>
          {displayNameOf(person)}
        </Typography>
        <Typography
          variant="caption"
          noWrap
          sx={{
            display: "block",
            color: person.status === "busy" ? "error.main" : "text.secondary",
            fontWeight: person.status === "busy" ? 700 : 500,
            lineHeight: 1.35,
          }}
        >
          {insight || (canSchedule ? "Clique para agendar" : "Disponível")}
        </Typography>
      </Box>
      {hasStrip ? (
        <DayStrip
          blocks={person.todayBlocks}
          status={person.status}
          theme={theme}
          width={compact ? 84 : 108}
        />
      ) : canSchedule ? (
        <Typography
          variant="caption"
          color="primary"
          fontWeight={700}
          sx={{ flexShrink: 0, display: { xs: "none", sm: "block" } }}
        >
          Agendar
        </Typography>
      ) : null}
    </Stack>
  );
}

function PeopleList({ people, theme, compact, onSchedule, showStrip = true }) {
  return (
    <Stack spacing={0.15} sx={{ px: compact ? 0.75 : 1, py: 0.5 }}>
      {people.map((person) => (
        <PersonRow
          key={person.userId}
          person={person}
          theme={theme}
          compact={compact}
          onSchedule={onSchedule}
          showStrip={showStrip}
        />
      ))}
    </Stack>
  );
}

function AvailableCluster({ people, theme, compact, expanded, onToggleExpand, onSchedule }) {
  const names = people.map(displayNameOf);
  const preview = names.slice(0, 4).join(", ");
  const extra = names.length - 4;
  const label = extra > 0 ? `${preview} e mais ${extra}` : preview;
  const size = compact ? 28 : 32;

  if (expanded) {
    return (
      <Box>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: compact ? 1.25 : 1.5, pt: 0.75, pb: 0.25 }}
        >
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            {people.length} {people.length === 1 ? "pessoa" : "pessoas"}
          </Typography>
          <Button
            size="small"
            onClick={onToggleExpand}
            endIcon={<ExpandLessIcon fontSize="small" />}
            sx={{ textTransform: "none", fontWeight: 700, minHeight: 26, px: 1 }}
          >
            Recolher
          </Button>
        </Stack>
        <PeopleList people={people} theme={theme} compact={compact} onSchedule={onSchedule} showStrip={false} />
      </Box>
    );
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{ px: compact ? 1 : 1.25, py: compact ? 0.85 : 1 }}
    >
      <Box
        onClick={onToggleExpand}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onToggleExpand();
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          minWidth: 0,
          flex: 1,
          cursor: "pointer",
          borderRadius: 2,
          px: 0.5,
          py: 0.35,
          "&:hover": {
            bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.1 : 0.04),
          },
        }}
      >
        <AvatarGroup
          max={6}
          sx={{
            "& .MuiAvatar-root": {
              width: size,
              height: size,
              fontSize: 11,
              fontWeight: 800,
              borderWidth: 2,
            },
          }}
        >
          {people.map((person) => {
            const name = displayNameOf(person);
            const tone = avatarTone(name, theme);
            return (
              <Avatar
                key={person.userId}
                alt={name}
                sx={{
                  bgcolor: alpha(tone, theme.palette.mode === "dark" ? 0.28 : 0.14),
                  color: tone,
                }}
              >
                {getInitials(name)}
              </Avatar>
            );
          })}
        </AvatarGroup>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" color="text.secondary" noWrap sx={{ lineHeight: 1.35 }}>
            {label}
          </Typography>
          <Typography variant="caption" color="primary" fontWeight={700}>
            Ver lista completa
          </Typography>
        </Box>
      </Box>
      <Button
        size="small"
        onClick={onToggleExpand}
        endIcon={<ExpandMoreIcon fontSize="small" />}
        sx={{ textTransform: "none", fontWeight: 700, flexShrink: 0, minHeight: 28 }}
      >
        Expandir
      </Button>
    </Stack>
  );
}

export default function AgendaAvailabilityPanel({ range: _range, compact = false, onScheduleWith }) {
  const theme = useTheme();
  const [open, setOpen] = useState(() => readAvailabilityPanelOpen(!compact));
  const [onlyAvailable, setOnlyAvailable] = useState(false);
  const [listExpanded, setListExpanded] = useState(false);
  const { data: usersData } = useUsers(1, 100, "", "humanos");
  const userIds = useMemo(
    () => (usersData?.users || []).map((u) => Number(u.id)).filter((id) => Number.isFinite(id)),
    [usersData]
  );

  // Sempre "agora / hoje" — não seguir o mês/semana do calendário (evita dizer livre
  // enquanto o compromisso do dia atual está ocupado).
  const todayKey = dayjs().format("YYYY-MM-DD");
  const from = `${todayKey} 00:00:00`;
  const to = `${todayKey} 23:59:59`;

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery(
    ["agenda-availability", todayKey, userIds.join(",")],
    async () =>
      unwrap(
        await fetchAgendaAvailability({
          from,
          to,
          userIds: userIds.slice(0, 40),
        })
      ) || { users: [] },
    { enabled: open && userIds.length > 0, staleTime: 60_000, keepPreviousData: true }
  );

  const people = useMemo(() => {
    const list = (data?.users || []).map((user) => {
      const availability = getPersonAvailability(user);
      return { ...user, ...availability };
    });
    return list.sort((a, b) => {
      const rank = statusSortRank(a.status) - statusSortRank(b.status);
      if (rank !== 0) return rank;
      return displayNameOf(a).localeCompare(displayNameOf(b), "pt-BR");
    });
  }, [data?.users]);

  const busyCount = people.filter((p) => p.status === "busy").length;
  const freeCount = people.filter((p) => p.status === "free").length;
  const laterCount = people.filter((p) => p.status === "later").length;
  const reauthPeople = people.filter((p) => p.needsReauth);
  const allClear = busyCount === 0 && laterCount === 0 && people.length > 0;
  const canSchedule = typeof onScheduleWith === "function";
  const summary = buildSummaryCopy({
    busyCount,
    laterCount,
    freeCount,
    total: people.length,
    canSchedule,
    reauthCount: reauthPeople.length,
  });

  const attentionPeople = useMemo(
    () => people.filter((p) => p.status === "busy" || p.status === "later"),
    [people]
  );
  const freePeople = useMemo(() => people.filter((p) => p.status === "free"), [people]);

  const visibleAttention = onlyAvailable
    ? attentionPeople.filter((p) => p.status !== "busy")
    : attentionPeople;

  const handleSchedule = canSchedule
    ? (person) => {
        onScheduleWith({
          userId: person.userId,
          name: person.name,
          email: person.email,
        });
      }
    : undefined;

  const collapsedHint =
    busyCount > 0
      ? `${busyCount} em compromisso`
      : laterCount > 0
        ? `${laterCount} com horário ainda hoje`
        : people.length
          ? "Todos livres"
          : null;

  return (
    <Box sx={{ mt: compact ? 0.75 : 1, mb: compact ? 0.5 : 0.75 }}>
      <Button
        size="small"
        startIcon={<GroupsOutlinedIcon fontSize="small" />}
        endIcon={open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        onClick={() =>
          setOpen((v) => {
            const next = !v;
            writeAvailabilityPanelOpen(next);
            return next;
          })
        }
        sx={{ textTransform: "none", fontWeight: 700, px: 0.75 }}
      >
        Quem está disponível
        {!open && collapsedHint ? (
          <Box
            component="span"
            sx={{
              ml: 1,
              px: 0.9,
              py: 0.15,
              borderRadius: 99,
              fontSize: 11,
              fontWeight: 700,
              bgcolor:
                busyCount > 0
                  ? alpha(theme.palette.error.main, 0.12)
                  : alpha(theme.palette.success.main, 0.12),
              color: busyCount > 0 ? "error.main" : "success.dark",
            }}
          >
            {collapsedHint}
          </Box>
        ) : null}
      </Button>

      <Collapse in={open}>
        <Box
          sx={{
            mt: 0.75,
            borderRadius: 2.5,
            border: 1,
            borderColor: alpha(theme.palette.divider, 0.9),
            bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.45 : 0.92),
            boxShadow: `0 1px 0 ${alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.2 : 0.03)}`,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: compact ? 1.25 : 1.5,
              pt: compact ? 1.1 : 1.35,
              pb: 1,
              borderBottom: people.length ? 1 : 0,
              borderColor: "divider",
              background: `linear-gradient(180deg, ${alpha(
                theme.palette.primary.main,
                theme.palette.mode === "dark" ? 0.08 : 0.04
              )} 0%, transparent 100%)`,
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              spacing={1}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight={800} sx={{ letterSpacing: -0.1, lineHeight: 1.25 }}>
                  {isLoading ? "Checando agendas…" : summary.title}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.35, lineHeight: 1.4 }}>
                  {summary.detail}
                  {isFetching && !isLoading ? " · atualizando" : ""}
                </Typography>
              </Box>

              {busyCount > 0 ? (
                <Button
                  size="small"
                  onClick={() => setOnlyAvailable((v) => !v)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 99,
                    px: 1.25,
                    minHeight: 28,
                    flexShrink: 0,
                    color: onlyAvailable ? "primary.contrastText" : "text.secondary",
                    bgcolor: onlyAvailable
                      ? "primary.main"
                      : alpha(theme.palette.text.primary, 0.05),
                    "&:hover": {
                      bgcolor: onlyAvailable
                        ? "primary.dark"
                        : alpha(theme.palette.text.primary, 0.09),
                    },
                  }}
                >
                  {onlyAvailable ? "Ver ocupados também" : "Ocultar ocupados"}
                </Button>
              ) : null}
            </Stack>
          </Box>

          <Box
            sx={{
              maxHeight: compact ? (listExpanded ? 280 : 240) : listExpanded ? 360 : 320,
              overflowY: "auto",
            }}
          >
            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={22} />
              </Box>
            ) : isError ? (
              <Alert
                severity="error"
                sx={{ m: 1 }}
                action={
                  <Button color="inherit" size="small" onClick={() => refetch()}>
                    Tentar de novo
                  </Button>
                }
              >
                {getApiErrorMessage(error, "Não foi possível carregar quem está disponível.")}
              </Alert>
            ) : !people.length ? (
              <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 2 }}>
                Nenhum colaborador encontrado neste espaço.
              </Typography>
            ) : allClear ? (
              <AvailableCluster
                people={people}
                theme={theme}
                compact={compact}
                expanded={listExpanded}
                onToggleExpand={() => setListExpanded((v) => !v)}
                onSchedule={handleSchedule}
              />
            ) : (
              <Stack spacing={0}>
                {visibleAttention.length ? (
                  <Box sx={{ py: 0.5 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={700}
                      sx={{ px: compact ? 1.5 : 1.75, pt: 0.5, pb: 0.25, display: "block", letterSpacing: 0.2 }}
                    >
                      {onlyAvailable ? "Livres, com horário depois" : "Precisa de atenção"}
                    </Typography>
                    <PeopleList
                      people={visibleAttention}
                      theme={theme}
                      compact={compact}
                      onSchedule={handleSchedule}
                      showStrip
                    />
                  </Box>
                ) : null}

                {freePeople.length ? (
                  <Box
                    sx={{
                      borderTop: visibleAttention.length ? 1 : 0,
                      borderColor: "divider",
                      bgcolor: alpha(theme.palette.success.main, theme.palette.mode === "dark" ? 0.05 : 0.03),
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={700}
                      sx={{ px: compact ? 1.5 : 1.75, pt: 0.85, display: "block", letterSpacing: 0.2 }}
                    >
                      Livres agora
                    </Typography>
                    <AvailableCluster
                      people={freePeople}
                      theme={theme}
                      compact={compact}
                      expanded={listExpanded}
                      onToggleExpand={() => setListExpanded((v) => !v)}
                      onSchedule={handleSchedule}
                    />
                  </Box>
                ) : null}

                {onlyAvailable && !freePeople.length && !visibleAttention.length ? (
                  <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 1.5 }}>
                    Ninguém livre no momento.
                  </Typography>
                ) : null}
              </Stack>
            )}
          </Box>

          {reauthPeople.length ? (
            <Tooltip title={reauthPeople.map(displayNameOf).join(", ")}>
              <Stack
                direction="row"
                alignItems="flex-start"
                spacing={1}
                sx={{
                  px: compact ? 1.25 : 1.5,
                  py: 0.85,
                  borderTop: 1,
                  borderColor: "divider",
                  bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === "dark" ? 0.08 : 0.05),
                  cursor: "help",
                }}
              >
                <SyncProblemOutlinedIcon sx={{ fontSize: 16, color: "warning.main", mt: 0.15 }} />
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1, lineHeight: 1.4 }}>
                  {reauthPeople.length === 1
                    ? `${displayNameOf(reauthPeople[0])} precisa reconectar o Google.`
                    : `${reauthPeople.length} pessoas precisam reconectar o Google.`}{" "}
                  Passe o mouse para ver quem.
                </Typography>
              </Stack>
            </Tooltip>
          ) : null}
        </Box>
      </Collapse>
    </Box>
  );
}
